import React, { useEffect, useState } from 'react';
import { CircularProgress, Paper } from '@material-ui/core';
import styled from 'styled-components';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SearchDocumentForm from '../components/forms/SearchDocumentForm';
import TaxIdSearchForm from '../components/forms/TaxIdSearchForm';
import BackButton from '../components/BackButton';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import { getDetails } from '../api/api';
import TopHeader from '../components/TopHeader';
import GalpUserMenu from '../components/GalpUserMenu';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  width: 100dvw;
  background: ${(props) => props.theme?.backgroundColor || '#f5f6f8'};
  padding-bottom: 4rem; /* spacing for back button */
  overflow-x: hidden;
`;

const Header = styled.div`
  text-align: center;
  position: relative;
  font-weight: 700;
  font-size: 1.3rem;
  margin-top: 0.6rem;
  margin-bottom: 0.4rem;
  color: ${(props) => props.theme.topHeaderTextColor || '#fff'};
`;

const Container = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 0 1rem;
  box-sizing: border-box;

  @media (max-width: 600px) {
    max-width: 100%;
    padding: 0 0.7rem;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: rgba(255,255,255,0.65);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(60, 80, 120, 0.12);

  & > div, & > span {
    background: rgba(255,255,255,0.85);
    border-radius: 18px;
    box-shadow: 0 2px 8px rgba(60,80,120,0.10);
    padding: 2rem 2.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.2rem;
    min-width: 220px;
  }

  .MuiCircularProgress-root {
    width: 3rem !important;
    height: 3rem !important;
    color: ${(props) => props.theme.loadingIconColor || '#1976d2'} !important;
    margin-bottom: 0.7rem;
  }

  .loading-text {
    font-size: 1.15rem;
    color: #222;
    text-align: center;
    font-weight: 500;
    letter-spacing: 0.01em;
  }

  @media (max-width: 600px) {
    & > div, & > span {
      padding: 1.2rem 1rem;
      min-width: 140px;
    }
    .MuiCircularProgress-root {
      width: 2.2rem !important;
      height: 2.2rem !important;
    }
    .loading-text {
      font-size: 1rem;
    }
  }
`;

const SegmentWrapper = styled.div`
  width: 100%;
  background: #eef0f4;
  padding: 0.25rem;
  border-radius: 12px;
  display: flex;
  gap: 0.3rem;
  margin-top: 2rem;
`;

const SegmentButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 0.75rem 0.5rem;
  border-radius: 10px;
  border: none;
  background: ${(props) => {
    if (props.disabled) return '#e0e0e0';
    if (props.active) return props.theme.buttonColor;
    return 'transparent';
  }};
  color: ${(props) => {
    if (props.disabled) return '#aaa';
    if (props.active) return '#fff';
    return '#555';
  }};
  font-size: 1rem;
  font-weight: 600;
  min-height: 44px;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  position: relative;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  box-shadow: ${(props) => {
    if (props.disabled) return 'none';
    if (props.active) return '0 2px 8px rgba(25, 118, 210, 0.10)';
    return 'none';
  }};

  &:active {
    opacity: ${(props) => (props.disabled ? 0.7 : 0.85)};
  }
`;

const ContentWrapper = styled(Paper)`
  padding: 1rem 0.7rem;
  margin-top: 2rem;
  border-radius: 12px !important;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;

  @media (max-width: 600px) {
    padding: 0.75rem 0.55rem;
    margin-top: 1.2rem;
  }
`;

const BgTop = styled.div`
  background: ${(props) => props.theme.noVmViewBGColor};
  height: 22%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

const DocumentSearchView = ({ history, location }) => {
  const { theme, themeName } = useTheme();
  const routerHistory = useHistory();
  const isHelexia = themeName === 'helexia';
  const userCtx = useUser();
  const [userDetails, setUserDetails] = useState<{ taxId?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState<'card' | 'taxid'>('card');
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [searchHasError, setSearchHasError] = useState(true);
  const { vendingMachineId } = location.state || {};
  const isGalp = themeName === 'galp';

  const galpInitials = (() => {
    const authUser = (userCtx.user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  useEffect(() => {
    setShowOverlay(loading);
  }, [loading]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (userCtx.user?.token) {
        try {
          const details = await getDetails(userCtx.user.token);
          setUserDetails(details);
        } catch {
          setUserDetails(null);
        }
      }
    };
    fetchDetails();
  }, [userCtx.user]);

  if (isGalp) {
    const isCardSearch = selectedSearch === 'card';
    const taxidDisabled = !userDetails || !userDetails.taxId;
    const galpTabs = [
      { key: 'card' as const, label: i18n.t('search_by_card'), disabled: false },
      { key: 'taxid' as const, label: i18n.t('search_by_taxid'), disabled: taxidDisabled },
    ];

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
      >
        {showOverlay && !loadingDocs && (
          <LoadingOverlay theme={theme}>
            <div>
              <CircularProgress color="primary" />
              <span className="loading-text">
                <Trans i18nKey="please_wait" components={{ br: <br />, b: <strong /> }} />
              </span>
            </div>
          </LoadingOverlay>
        )}

        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => routerHistory.goBack()}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
          }}
          >
            {i18n.t('search_documents')}
          </p>
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Body */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '24px 16px',
          minHeight: 0,
          boxSizing: 'border-box',
        }}
        >
          <div style={{ display: 'flex', width: '100%', flexShrink: 0 }}>
            {galpTabs.map((t) => {
              const active = selectedSearch === t.key;
              let tabColor = '#616061';
              if (t.disabled) tabColor = '#c0bfc0';
              else if (active) tabColor = '#111110';
              return (
                <button
                  key={t.key}
                  type="button"
                  disabled={t.disabled}
                  onClick={() => !t.disabled && setSelectedSearch(t.key)}
                  style={{
                    flex: 1,
                    height: '36px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active ? '2px solid #be2e00' : '1px solid #eee',
                    color: tabColor,
                    fontFamily: theme.bodyFont,
                    fontSize: '14px',
                    cursor: t.disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div style={{
            flex: 1,
            overflowY: 'auto',
            marginTop: '20px',
            minHeight: 0,
          }}
          >
            {isCardSearch ? (
              <SearchDocumentForm
                vendingMachineId={vendingMachineId}
                setLoading={setLoading}
                userDetails={userDetails}
                isGalp
                onValidityChange={setSearchHasError}
              />
            ) : (
              userDetails && (
                <TaxIdSearchForm
                  setLoading={setLoading}
                  userDetails={userDetails}
                  loadingDocs={loadingDocs}
                  setLoadingDocs={setLoadingDocs}
                />
              )
            )}
          </div>
        </div>

        {/* Footer — only the card search has a submit button */}
        {isCardSearch && (
          <div style={{
            borderTop: '1px solid #bababa',
            background: 'white',
            height: '85px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 32px',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
          >
            <button
              type="submit"
              form="search"
              disabled={searchHasError}
              style={{
                height: '36px',
                width: '100%',
                borderRadius: '57px',
                background: searchHasError ? '#df9680' : theme.buttonColor,
                border: 'none',
                color: theme.buttonTextColor || 'white',
                fontFamily: theme.bodyFont,
                fontSize: '14px',
                cursor: searchHasError ? 'not-allowed' : 'pointer',
                boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
              }}
            >
              {i18n.t('search')}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (isHelexia) {
    const isCardSearch = selectedSearch === 'card';

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        background: 'white',
      }}
      >
        <TopHeader
          title="documents_title"
          userLogo
          showBackButton
          onBack={() => routerHistory.goBack()}
          backButtonColor={theme.titleColor}
          showShadow
        />

        {/* Gradient title area */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(rgb(252, 251, 210), rgb(251, 217, 4))',
          overflow: 'hidden',
        }}
        >
          <div style={{ padding: '40px 12px 40px', textAlign: 'center' }}>
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '32px',
              color: '#004e74',
              margin: 0,
            }}
            >
              {i18n.t('search_documents')}
            </p>
          </div>

          {/* Grey panel */}
          <div style={{
            flex: 1,
            background: '#e8e8e8',
            borderRadius: '20px 20px 0 0',
            padding: '20px 12px',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            paddingBottom: '66px',
          }}
          >
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '9px', width: '100%', marginBottom: '20px' }}>
              {(['card', 'taxid'] as const).map((tab) => {
                const isSelected = selectedSearch === tab;
                const isDisabled = tab === 'taxid' && (!userDetails || !userDetails.taxId);
                return (
                  <button
                    key={tab}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => !isDisabled && setSelectedSearch(tab)}
                    style={{
                      flex: 1,
                      background: isDisabled ? '#b0c4cc' : isSelected ? '#004e74' : '#8babba',
                      border: 'none',
                      borderRadius: '12px',
                      height: '36px',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      fontFamily: 'Geologica, sans-serif',
                      fontWeight: 400,
                      fontSize: '13px',
                      letterSpacing: '-0.42px',
                      color: 'white',
                      opacity: isDisabled ? 0.6 : 1,
                      boxSizing: 'border-box',
                    }}
                  >
                    {i18n.t(tab === 'card' ? 'search_by_card' : 'search_by_taxid')}
                  </button>
                );
              })}
            </div>

            {/* Form content */}
            {isCardSearch ? (
              <SearchDocumentForm
                vendingMachineId={vendingMachineId}
                setLoading={setLoading}
                userDetails={userDetails}
                isHelexia
              />
            ) : (
              userDetails && (
                <TaxIdSearchForm
                  setLoading={setLoading}
                  userDetails={userDetails}
                  loadingDocs={loadingDocs}
                  setLoadingDocs={setLoadingDocs}
                />
              )
            )}

            {/* Search button at bottom of grey panel */}
            {isCardSearch && (
              <button
                type="submit"
                form="search"
                style={{
                  marginTop: 'auto',
                  background: 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
                  borderRadius: '12px',
                  height: '36px',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  letterSpacing: '-0.42px',
                  width: '100%',
                  flexShrink: 0,
                }}
              >
                {i18n.t('search')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Root theme={theme}>
      {showOverlay && !loadingDocs && (
        <LoadingOverlay theme={theme}>
          <div>
            <CircularProgress color="primary" />
            <span className="loading-text">
              <Trans i18nKey="please_wait" components={{ br: <br />, b: <strong /> }} />
            </span>
          </div>
        </LoadingOverlay>
      )}

      <BgTop theme={theme} />

      <TopHeader
        showVm={false}
        userLogo
        color={theme.iconColor}
        style={{ paddingLeft: '1em', marginBottom: '0.3rem' }}
      />

      <Header theme={theme}>{i18n.t('search_documents')}</Header>

      <Container>
        <SegmentWrapper>
          <SegmentButton
            active={selectedSearch === 'card'}
            onClick={() => setSelectedSearch('card')}
            theme={theme}
          >
            {i18n.t('search_by_card')}
          </SegmentButton>

          <SegmentButton
            active={selectedSearch === 'taxid'}
            onClick={() => setSelectedSearch('taxid')}
            theme={theme}
            disabled={!userDetails || !userDetails.taxId}
          >
            {i18n.t('search_by_taxid')}
          </SegmentButton>
        </SegmentWrapper>

        <ContentWrapper>
          {selectedSearch === 'card' ? (
            <SearchDocumentForm
              vendingMachineId={vendingMachineId}
              setLoading={setLoading}
              userDetails={userDetails}
            />
          ) : (
            (userDetails && (
              <TaxIdSearchForm
                setLoading={setLoading}
                userDetails={userDetails}
                loadingDocs={loadingDocs}
                setLoadingDocs={setLoadingDocs}
              />
            ))
          )}
        </ContentWrapper>
      </Container>

      <BackButton backHandler={() => history.goBack()} />
    </Root>
  );
};

export default DocumentSearchView;
